import React, { useMemo } from "react";
import { Box, Button } from "@mui/material";
import { useFormContext } from "react-hook-form";

import { useSessionStore } from "../../../services/hooks";
import { permissionsInclude } from "../../../services/utils";

const CardList = ({ activeTasks, useMinHeight, ...props }) => {
  const { watch } = useFormContext();
  const [itemID, passageContentArea, passageID] = watch([
    "item.id",
    "passage.contentArea",
    "passage.id",
  ]);

  const [workspace, workspaceStore] = useSessionStore("workspace");

  const cards = useMemo(() => {
    if (!workspace?.cards) return [];

    return [
      {
        available: passageID,
        key: "ItemSetSummaryCard",
        title: "Item Set Summary",
      },
      {
        available:
          passageID &&
          !permissionsInclude({ activeTasks, type: "passage" })(
            "noPassageContent",
          ),
        key: "PassageContentCard",
        title: "Passage Content",
      },
      {
        available: passageID,
        key: "PassagePreviewCard",
        title: "Passage Preview",
      },
      {
        available:
          passageID &&
          !permissionsInclude({ activeTasks, type: "passage" })(
            "noPassageMetadata",
          ),
        key: "PassageMetadataCard",
        title: "Passage Metadata",
      },
      {
        available:
          passageID &&
          !permissionsInclude({ activeTasks, type: "passage" })(
            "noPassageAssets",
          ),
        key: "PassageAssetsCard",
        title: "Passage Assets",
      },
      {
        available:
          passageID &&
          passageContentArea === "English" &&
          !permissionsInclude({ activeTasks, type: "passage" })(
            "noReferencePassage",
          ),
        key: "PassageReferenceContentCard",
        title: "Passage Reference Content",
      },
      {
        available: passageID,
        key: "PassageTasksCard",
        title: "Passage Tasks",
      },
      {
        available:
          passageID &&
          permissionsInclude({ activeTasks, type: "passage" })(
            "viewPassageImageAssets",
          ),
        key: "ViewPassageImageAssetsCard",
        title: "View Passage Image Assets",
      },
      {
        available:
          itemID &&
          !permissionsInclude({ activeTasks, type: "item" })("noItemContent"),
        key: "ItemContentCard",
        title: "Item Content",
      },
      {
        available: itemID,
        key: "ItemPreviewCard",
        title: "Item Preview",
      },
      {
        available:
          itemID &&
          !permissionsInclude({ activeTasks, type: "item" })("noItemMetadata"),
        key: "ItemMetadataCard",
        title: "Item Metadata",
      },
      {
        available:
          itemID &&
          !permissionsInclude({ activeTasks, type: "item" })("noItemAssets"),
        key: "ItemAssetsCard",
        title: "Item Assets",
      },
      {
        available:
          itemID &&
          permissionsInclude({ activeTasks, type: "item" })(
            "viewItemImageAssets",
          ),
        key: "ViewItemImageAssetsCard",
        title: "View Item Image Assets",
      },
      {
        available: itemID,
        key: "ItemTasksCard",
        title: "Item Tasks",
      },
    ].map((card) => ({
      ...card,
      open: workspace.cards.some((c) => card.key === c.i),
    }));
  }, [activeTasks, itemID, passageContentArea, passageID, workspace?.cards]);

  const openCard = (name, item, passage) => () => {
    workspaceStore.openCard(name, item, passage, { useMinHeight });
  };

  return (
    <Box
      sx={{
        alignItems: "center",
        display: "flex",
        flexGrow: 1,
        height: 50,
        justifyContent: "space-between",
      }}
      {...props}
    >
      {cards
        .filter(({ available }) => available)
        .map(({ key, open, title }) => (
          <Button
            key={key}
            disabled={open}
            onClick={openCard(key, itemID, passageID)}
            size="small"
            variant="text"
          >
            {title}
          </Button>
        ))}
    </Box>
  );
};

export { CardList };
